<template>
  <LoadingSpinner v-if="isLoadingData" />
  <div v-else class="show-budget-template">
    <AppPageHeader title="Edit Budget Template" />

    <div class="px-5 pb-5 mb-5">
      <CreateBudgetItem
        id="0"
        v-model="lineItemTemplate"
        hide-save-template
        @can-submit="canSubmit = $event"
      />
    </div>

    <hr class="mt-6">

    <div class="flex justify-end gap-2 my-3">
      <BaseButton
        text="Cancel"
        :disabled="isUpdating"
        :class="{ 'cursor-not-allowed': isUpdating }"
        @click="$router.push({ name: 'ListBudgetTemplates' })"
      />
      <BaseButton
        variant="primary"
        text="Save"
        :disabled="!canSubmit || isUpdating"
        :class="{ 'cursor-not-allowed': !canSubmit || isUpdating }"
        @click="updateBudgetTemplate(lineItemTemplate)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BudgetItem } from '@/models/budget'
import BaseButton from '@/components/base/BaseButton.vue'
import CreateBudgetItem from '@/modules/Budget/components/CreateBudgetItem.vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import BudgetTemplateService from '@/services/BudgetTemplateService'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useRoute, useRouter } from 'vue-router'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import { useNotification } from '@kyvg/vue3-notification'
import { ref, watch } from 'vue'

const budgetTemplateId = useRoute().params.id as string || ''
const $router = useRouter()
const { notify } = useNotification()
const budgetTemplateService = new BudgetTemplateService()
const queryClient = useQueryClient()
const canSubmit = ref(false)
const lineItemTemplate = ref<BudgetItem>(new BudgetItem())

const { data: budgetTemplate, isPending: isLoadingData } = useQuery({
  queryKey: ['budgetTemplate', budgetTemplateId],
  queryFn: async () => budgetTemplateService.find(budgetTemplateId)
})

watch(isLoadingData, (isLoading) => {
  if (!isLoading && budgetTemplate.value) {
    lineItemTemplate.value = JSON.parse(JSON.stringify(budgetTemplate.value))
  }
}, { immediate: true })

const { mutateAsync: updateBudgetTemplate, isPending: isUpdating } = useMutation({
  mutationFn: (budgetTemplate: BudgetItem) => budgetTemplateService.update(budgetTemplateId, budgetTemplate),
  onSuccess: (updatedBudgetTemplate: BudgetItem) => {
    queryClient.setQueryData(['budgetTemplate', updatedBudgetTemplate.id], updatedBudgetTemplate)
    queryClient.invalidateQueries({ queryKey: ['budgetTemplates'] })

    notify({
      type: 'success',
      title: 'Budget Template updated',
      text: 'Budget Template updated successfully'
    })
    $router.push({ name: 'ListBudgetTemplates' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to update the budget template'
    })
  }
})
</script>
